import React, { useState, useRef, useEffect } from "react";
import { Box, Select, MenuItem, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import { useBusParkFirebase } from "./Bus_Park_FireBase";
import { useNavigate } from "react-router-dom";
import InConstruction from "./InConstrution";
import NavBarAdm from "../../adm/navbarAdm";
import { getFirestore, collection, query, where, getDocs } from "firebase/firestore";


export default function Bus_Park() {
  const navigate = useNavigate();

  // new helper to navigate to the print page (absolute path)
  const handleNavigate = (path = "/bus_park_print") => {
    navigate(path);
  };

  // new helper to navigate to the add bus page (absolute path)
  const handleNavigateAdd = (path = "/adm/bus_park_add") => {
    navigate(path);
  };

  // new layout: columns A..M and top row O
  const columnsSpec = [
    { letter: "A", count: 5, height: 195 },
    { letter: "B", count: 5, height: 195 },
    { letter: "C", count: 5, height: 195 },
    { letter: "D", count: 5, height: 195 },
    { letter: "E", count: 7, height: 140 },
    { letter: "F", count: 7, height: 140 },
    { letter: "G", count: 7, height: 140 },
    { letter: "H", count: 7, height: 140 },
    { letter: "I", count: 7, height: 140 },
    { letter: "J", count: 7, height: 140 },
    { letter: "K", count: 7, height: 140 },
    { letter: "L", count: 10, height: 140, rotated: true },
    { letter: "M", count: 10, height: 140, rotated: true },
  ];

  // Build labels in order: A1..A5, B1..B5, ..., M1..M10, then top row N1..N16 (expanded)
  const labels = React.useMemo(() => {
    const arr = [];
    columnsSpec.forEach(col => {
      for (let i = 1; i <= col.count; i++) arr.push(`${col.letter}${i}`);
    });
    for (let i = 1; i <= 16; i++) arr.push(`N${i}`); // extra top row (N1..N16)
    return arr;
  }, []);

  const labelToIndex = React.useMemo(() => {
    const m = {};
    labels.forEach((l, idx) => { m[l] = idx; });
    return m;
  }, [labels]);

  // total places = labels.length
  const totalPlaces = labels.length;

  // use hook with new total
  const { docs, setDocs, parking, setParking, loading, BUS_NUMBERS, getAvailableNumbers, handleDelete, handleSelect } =
    useBusParkFirebase(totalPlaces, labels);

  // compute available buses for a given place index:
  // show buses that are not currently assigned to any place (docs[].place falsy)
  // but allow the current bus at this index to remain selectable.
  const getAvailableBusesForIndex = (index) => {
    const occupied = new Set((docs || [])
      .filter(d => d && d.place !== undefined && d.place !== null && String(d.place) !== "")
      .map(d => String(d.bus)));
    const current = parking?.[index]?.bus;
    if (current) occupied.delete(String(current));
    return (Array.isArray(BUS_NUMBERS) ? BUS_NUMBERS.slice() : [])
      .map(b => String(b))
      .filter(b => !occupied.has(b))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  };

  // Dialog state & handlers for editing status/info (opened from click on bus number)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogIndex, setDialogIndex] = useState(null);
  const [dialogBus, setDialogBus] = useState("");
  const [dialogStatus, setDialogStatus] = useState(0);
  const [dialogInfo, setDialogInfo] = useState("");
  const [dialogLoading, setDialogLoading] = useState(false);

  // open dialog and load latest doc from Firestore (place OR bus) to populate status/info
  const openEditDialog = async (index) => {
    setDialogLoading(true);
    try {
      const db = getFirestore();
      // use place label (string) not numeric index
      const placeLabel = labels[index];
      let foundData = null;
      let foundBus = "";

      // 1) try to find document by place (string)
      const colRef = collection(db, "BusParkPlaces");
      const qPlace = query(colRef, where("place", "==", String(placeLabel)));
      const snapPlace = await getDocs(qPlace);
      if (!snapPlace.empty) {
        const docSnap = snapPlace.docs[0];
        foundData = docSnap.data();
        foundBus = String(foundData.bus ?? "");
      } else {
        // 2) fallback: try to find by bus (from parking or docs cache)
        const entry = parking?.[index] ?? null;
        const busCandidate = entry ? String(entry.bus ?? "") : "";
        const busToQuery = busCandidate || (docs && docs.length ? String((docs.find(d => d && String(d.place) === String(placeLabel)) || {}).bus ?? "") : "");
        if (busToQuery) {
          const qBus = query(colRef, where("bus", "==", busToQuery));
          const snapBus = await getDocs(qBus);
          if (!snapBus.empty) {
            const docSnap = snapBus.docs[0];
            foundData = docSnap.data();
            foundBus = String(foundData.bus ?? "");
          }
        }
      }

      // fallback to local snapshot if no doc found
      if (!foundData) {
        const entry = parking?.[index] ?? null;
        foundData = entry ?? {};
        foundBus = String(foundData.bus ?? "");
      }

      setDialogIndex(index);
      setDialogBus(foundBus);
      setDialogStatus(foundData && foundData.status !== undefined ? Number(foundData.status) : 0);
      setDialogInfo(foundData && foundData.info !== undefined ? String(foundData.info) : "");
      setDialogOpen(true);
    } catch (err) {
      console.error("openEditDialog error:", err);
      // fallback to local snapshot
      const entry = parking?.[index] ?? null;
      setDialogIndex(index);
      setDialogBus(entry ? String(entry.bus ?? "") : "");
      setDialogStatus(entry ? (Number.isFinite(Number(entry.status)) ? Number(entry.status) : 0) : 0);
      setDialogInfo(entry ? String(entry.info ?? "") : "");
      setDialogOpen(true);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
  };

  const handleDialogSave = async () => {
    if (dialogIndex == null) return;
    if (!dialogBus || String(dialogBus).trim() === "") {
      alert("Kein Bus ausgewählt.");
      return;
    }

    setDialogLoading(true);
    const idx = dialogIndex;
    // ensure we pass label string as place
    const payload = { bus: dialogBus, place: labels[idx], status: Number(dialogStatus), info: dialogInfo };

    console.log("handleDialogSave -> about to call handleSelect; handleSelect type:", typeof handleSelect, "payload:", payload);

    try {
      const res = await handleSelect(idx, payload);
      console.log("handleDialogSave -> handleSelect result:", res);
      if (!res || res.ok !== true) {
        throw new Error(res?.message ?? "Speichern fehlgeschlagen");
      }

      // use returned values (server / hook) when available
      const usedStatus = res.payload?.status ?? Number(dialogStatus);
      const usedInfo = res.payload?.info ?? String(dialogInfo ?? "");
      const usedBus = res.payload?.bus ?? dialogBus;
      const usedPlace = res.payload?.place ?? idx + 1;

      // update local parking state
      setParking(prev => {
        const next = Array.isArray(prev) ? [...prev] : Array.from({ length: 91 }, () => null);
        next[idx] = { bus: usedBus, place: usedPlace, status: usedStatus, info: usedInfo };
        return next;
      });

      // update local docs cache if available
      if (typeof setDocs === "function") {
        setDocs(prev => {
          const list = prev ? [...prev] : [];
          const matchIdx = list.findIndex(d => d && (String(d.bus) === String(usedBus) || String(d.id) === String(usedBus)));
          const docEntry = { id: String(usedBus), bus: usedBus, place: usedPlace, status: usedStatus, info: usedInfo };
          if (matchIdx >= 0) {
            list[matchIdx] = { ...list[matchIdx], ...docEntry };
          } else {
            list.push(docEntry);
          }
          return list;
        });
      }

      setDialogOpen(false);
    } catch (err) {
      console.error("handleDialogSave error:", err);
      alert("Fehler beim Speichern: " + (err?.message ?? "siehe Konsole"));
    } finally {
      setDialogLoading(false);
    }
  };

  // Structure + UI state
  const col1 = Array(5).fill(null);
  const col2 = Array(5).fill(null);
  const col3to9 = Array(7 * 7).fill(null);
  const col10 = Array(10).fill(null);
  const col11 = Array(10).fill(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const printRef = useRef();

  // refs + state to align top extra row with rectangle id 5 (index 4)
  const containerRef = useRef(null);
  const alignRef = useRef(null); // attached to rectangle index 4
  const [topRowLeft, setTopRowLeft] = useState(88); // px fallback

  useEffect(() => {
    const computeLeft = () => {
      if (!containerRef.current || !alignRef.current) return;
      const parentRect = containerRef.current.getBoundingClientRect();
      const childRect = alignRef.current.getBoundingClientRect();
      const left = Math.max(0, Math.round(childRect.left - parentRect.left));
      setTopRowLeft(left);
    };

    // compute on mount and on resize
    computeLeft();
    window.addEventListener("resize", computeLeft);
    return () => window.removeEventListener("resize", computeLeft);
  }, []);

  useEffect(() => {
    console.log("parking (preview):", parking?.map((p,i)=> p ? { i, label: labels[i], bus: p.bus, status: p.status } : null).filter(Boolean).slice(0,80));
  }, [parking, labels]);

  // Helper to render a single parking spot
  const renderSpot = (index, width, height, rotate = 0, topOffset = 0) => {
    // assume each slot is an object: { bus, place, status } (or null)
    const entry = parking?.[index] ?? null;
    const displayNumber = entry ? (entry.bus ?? "") : "";
    const status = entry ? (Number.isFinite(Number(entry.status)) ? Number(entry.status) : 0) : 0;

    return (
      <Box
        key={index}
        ref={index === 4 ? alignRef : undefined} // attach ref to rectangle id 5 (index 4)
        sx={{
          width,
          height,
          bgcolor: "#006372ff",
          border: "3px solid #fff",
          borderRadius: 0,
          display: "block",
          position: "relative",
          margin: 0,
          transform: `rotate(${rotate}deg)`,
          mt: topOffset,
          boxSizing: "border-box",
          overflow: "visible",
          px: 0,
          py: 0
        }}
        onClick={e => {
          e.stopPropagation();
          setEditingIndex(index);
        }}
      >
        {/* Row layout container (three logical rows) */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "grid",
            gridTemplateRows: "auto 1fr auto" // row1: id, row2: select area, row3: selected number area
          }}
        >
          {/* Row 1: id (top-left) */}
          <Box sx={{ position: "absolute", top: 6, left: 6, zIndex: 4 }}>
            <Typography
              sx={{
                color: "#9e9e9e",
                fontSize: 32,
                fontWeight: 500,
                userSelect: "none"
              }}
            >
              {labels[index] ?? String(index + 1)}
            </Typography>
          </Box>

          {/* Row 2: Select field - positioned so it can overlap if needed */}
          <Select
            value={editingIndex === index ? (displayNumber || "") : ""}
            onChange={async (e) => {
              e.stopPropagation();
              const value = String(e.target.value);
              // Assign bus to slot WITHOUT changing status/info (status is only changed via dialog Save)
              try {
                // use place label (e.g. "A1") as string
                await handleSelect(index, { bus: value, place: labels[index] }); // labels[index] is e.g. "A3"
                // update local parking using docs/parking sync from hook (optimistic)
                setParking(prev => {
                  const next = Array.isArray(prev) ? [...prev] : Array.from({ length: 91 }, () => null);
                  // preserve any existing status/info already present for this bus in docs[] if available
                  const existing = docs?.find(d => String(d.bus) === value) ?? null;
                  next[index] = {
                    bus: value,
                    place: labels[index],
                    status: existing && existing.status !== undefined ? existing.status : (prev?.[index]?.status ?? 0),
                    info: existing && existing.info !== undefined ? existing.info : (prev?.[index]?.info ?? "")
                  };
                  return next;
                });
              } catch (err) {
                console.error("handleSelect (assign) failed:", err);
              } finally {
                setEditingIndex(null);
              }
            }}
            displayEmpty
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              bottom: "36px",         // leave space for number (bottom row)
              width: "86%",
              minHeight: 54,
              fontSize: 24,
              color: "#fff",
              bgcolor: "#dbdbdbff",
              borderRadius: 1,
              zIndex: 3,
              boxSizing: "border-box",
              "& .MuiSelect-select": {
                py: 0.25,
                px: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                fontSize: 24,
                color: "#fff",
                lineHeight: 1
              }
            }}
            MenuProps={{
              PaperProps: {
                style: { fontSize: 28, color: "#222" }
              }
            }}
            renderValue={() => ""}
          >
            {getAvailableBusesForIndex(index).map(num => (
              <MenuItem key={String(num)} value={String(num)} sx={{ fontSize: 28, color: "#222", fontWeight: "bold" }}>
                {String(num)}
              </MenuItem>
            ))}
          </Select>

          {/* Row 3: selected number - anchored to bottom; number from array styled with colored background based on status */}
          {displayNumber && (
            <Box
              onClick={e => {
                e.stopPropagation();
                // open dialog to edit status/info instead of toggling inline
                openEditDialog(index);
              }}
              sx={{
                position: "absolute",
                bottom: 2,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 4,
                bgcolor: status === 1 ? "#fff59d" : status === 2 ? "#f44336" : "#fff",
                border: "2px solid #000",
                borderRadius: "4px",
                px: 1,
                py: 0.25,
                boxSizing: "border-box",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minWidth: "40%",
                cursor: "pointer" // indicate clickable
              }}
            >
              <Typography sx={{ color: status === 2 ? "#fff" : "#000", fontSize: 24, fontWeight: "bold", textAlign: "center" }}>
                {displayNumber}
              </Typography>
            </Box>
          )}

          {/* Delete button stays top-right and above other elements */}
          {displayNumber && (
            <Box
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                color: "#fff",
                fontWeight: "bold",
                fontSize: 18,
                cursor: "pointer",
                zIndex: 5,
                px: 0.5,
                borderRadius: 1,
                bgcolor: "#d32f2f",
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              onClick={e => {
                e.stopPropagation();
                void handleDelete(index);
              }}
            >
              ×
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // Render rectangles for popup (only numbers, no select fields)
  const renderNumberSpot = (index, width, height, rotate = 0, topOffset = 0) => {
    const entry = parking?.[index] ?? null;
    const displayNumber = entry ? (entry.bus ?? "") : "";
    const status = entry ? (Number.isFinite(Number(entry.status)) ? Number(entry.status) : 0) : 0;

    return (
      <Box
        key={index}
        sx={{
          width,
          height,
          bgcolor: "#fff", // white rectangle
          border: "3px solid #000", // black border
          borderRadius: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          margin: 0,
          transform: `rotate(${rotate}deg)`,
          mt: topOffset,
          boxSizing: "border-box",
          overflow: "visible"
        }}
      >
        {/* id (label) shown in top-left as gray */}
        <Typography
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            color: "#9e9e9e",
            fontSize: 12,
            fontWeight: 500,
            userSelect: "none"
          }}
        >
          {labels[index] ?? String(index + 1)}
        </Typography>

        {displayNumber && (
          <Box
            onClick={e => {
              e.stopPropagation();
              // open status/info dialog instead of toggling inline
              openEditDialog(index);
            }}
            sx={{
              bgcolor: status === 1 ? "#fff59d" : status === 2 ? "#f44336" : "#fff",
              border: "2px solid #000",
              borderRadius: "4px",
              mt: "4px",            // move the number a bit lower inside the rectangle
              px: 1,
              py: 0.2,
              boxSizing: "border-box",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer" // indicate clickable
            }}
          >
            <Typography
              sx={{
                color: status === 2 ? "#fff" : "#000",
                fontSize: 18,
                fontWeight: "bold",
                textAlign: "center",
                letterSpacing: 1,
                userSelect: "none",
                lineHeight: 1
              }}
            >
              {displayNumber}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };


  // Layout without popup and button
  return (
    <>
    <NavBarAdm />
<InConstruction />



      <Box ref={containerRef} sx={{ overflow: "auto", p: 2 }}>
        {/* EXTRA TOP ROW: render one row of rectangles above the main grid.
            Only keep ids up to 91 (remove 92 and higher). This renders indices 79..90
            which correspond to ids 80..91.
        */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 0,
            justifyContent: "flex-start",
            mb: 3,
            // pl: `${topRowLeft}px`
          }}
        >
          {/* top row spec: single labels or grouped pairs that should be rotated & stacked */}
          {(() => {
            const spec = [
              "N1","N2","N3","N4","N5","N6",
              ["N7","N8"], // render rotated and stacked in one column
              ["N9","N10"], // render rotated and stacked in one column
              "N11","N12","N13","N14","N15"
              ,"N16"
            ];

            return spec.map((item, i) => {
              if (Array.isArray(item)) {
                // grouped column: two rotated rectangles stacked vertically
                return (
                  <Box
                    key={`group-${i}`}
                    sx={{
                      mt: -3.7,
                      mb: -6,
                      mx: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                      // pull the second rotated rectangle up to reduce space between the pair
                      "& > *:not(:first-child)": {
                        mt: -8
                      }
                    }}
                   >
                    {item.map(label => {
                      const idx = labelToIndex[label];
                      // rotate 90 degrees for grouped top-row items
                      return <Box key={label}>{renderSpot(idx, 80, 140, 90)}</Box>;
                    })}
                  </Box>
                );
              } else {
                const idx = labelToIndex[item];
                return (
                  <Box key={item} sx={{ mx: 0.25 }}>
                    {renderSpot(idx, 80, 140)}
                  </Box>
                );
              }
            });
          })()}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "row", gap: 0 }}>
          {/* Column 1 (render bottom-to-top so id 1 is bottom-left) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {
              // Column A (5) bottom-to-top -> render A5..A1 so bottom is A1
              [...Array(5)].map((_, ri) => {
                const label = `A${5 - ri}`;
                const idx = labelToIndex[label];
                return renderSpot(idx, 80, 195);
              })
            }
            {/* Date today immediately under column 1 */}
            <Typography sx={{ mt: 2, fontSize: 18, fontWeight: "bold", color: "#333", textAlign: "center" }}>
              {new Date().toLocaleDateString()}
            </Typography>
          </Box>

          {/* Column 2 (bottom-to-top, ids 6..10 bottom is id 6) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {
              // Column B bottom-to-top (B5..B1)
              [...Array(5)].map((_, ri) => {
                const label = `B${5 - ri}`;
                const idx = labelToIndex[label];
                return renderSpot(idx, 80, 195);
              })
            }
          </Box>

          {/* Column 3 (C) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {
              // Column C bottom-to-top (C5..C1)
              [...Array(5)].map((_, ri) => {
                const label = `C${5 - ri}`;
                const idx = labelToIndex[label];
                return renderSpot(idx, 80, 195);
              })
            }
          </Box>

          {/* Column 4 (D) */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {
              // Column D bottom-to-top (D5..D1)
              [...Array(5)].map((_, ri) => {
                const label = `D${5 - ri}`;
                const idx = labelToIndex[label];
                return renderSpot(idx, 80, 195);
              })
            }
          </Box>

          {/* Columns 5-11 (7 columns x 7 rows) bottom-to-top in each column */}
          <Box sx={{ display: "flex", flexDirection: "row", gap: 0 }}>
            {[..."EFGHIJK"].map((letter, colIdx) => (
              <Box key={letter} sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {
                  // each column letter has 7 slots, render bottom-to-top (e.g. E7..E1)
                  [...Array(7)].map((_, ri) => {
                    const label = `${letter}${7 - ri}`;
                    const idx = labelToIndex[label];
                    return renderSpot(idx, 80, 140);
                  })
                }
              </Box>
            ))}
          </Box>

          {/* Column 9: separator */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ width: 30, height: 990, bgcolor: "#8a3333ff", borderRadius: 0 }} />
            {[...Array(6)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 30,
                  height: 15,
                  bgcolor: "#8a3333ff",
                  borderRadius: 0,
                  mt: 1
                }}
              />
            ))}
          </Box>

          {/* Columns L & M, rotated (bottom-to-top) */}
          <Box sx={{ display: "flex", flexDirection: "row", mt: 2, ml: 5, gap: 0 }}>
            {/* Column L */}
            <Box sx={{ display: "flex", flexDirection: "column", mt: 0.6, gap: 0 }}>
              {
                [...Array(10)].map((_, ri) => {
                  const label = `L${10 - ri}`; // bottom-to-top L10..L1 so bottom is L1
                  const idx = labelToIndex[label];
                  return (
                    <Box key={label} sx={{ position: "relative", mr: 7, mb: -6 }}>
                      {renderSpot(idx, 80, 140, 75)}
                      {/* place blue separator one row ABOVE each pair: after L2, L4, L6, L8, L10 */}
                      {[0, 2, 4, 6, 8].includes(ri) && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: "50%",
                            bottom: -50,
                            transform: "translateX(-50%) rotate(75deg)",
                            width: 12,
                            height: 145,
                            bgcolor: "#1976d2",
                            borderRadius: 0,
                            mb: 0.2,
                            padding: 0,
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          <ElectricalServicesIcon sx={{ color: "#fff", fontSize: 18, transform: "rotate(-75deg)" }} />
                        </Box>
                      )}
                    </Box>
                  );
                })
              }
            </Box>

            {/* Column M */}
            <Box sx={{ display: "flex", flexDirection: "column", mt: -1, gap: 0 }}>
              {
                [...Array(10)].map((_, ri) => {
                  const label = `M${10 - ri}`;
                  const idx = labelToIndex[label];
                  return (
                    <Box key={label} sx={{ position: "relative", mb: -6, mt: ri === 0 ? "-64px" : 0 }}>
                      {renderSpot(idx, 80, 140, 75, ri === 0 ? 5 : 0)}
                      {/* same separator positioning for M column */}
                      {[0, 2, 4, 6, 8].includes(ri) && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: "50%",
                            bottom: -50,
                            transform: "translateX(-50%) rotate(75deg)",
                            width: 12,
                            height: 145,
                            bgcolor: "#1976d2",
                            borderRadius: 0,
                            mb: 0.2,
                            padding: 0,
                            zIndex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          <ElectricalServicesIcon sx={{ color: "#fff", fontSize: 18, transform: "rotate(-75deg)" }} />
                        </Box>
                      )}
                    </Box>
                  );
                })
              }
            </Box>
          </Box>
        </Box>

        {/* SHOW button now navigates to print page */}
        {/* translucent white background at bottom, top edge 5px above top of buttons */}
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            // height = bottomOffset (16px) + approx button height (48px) + 5px gap
            height: "calc(16px + 32px)",
            bgcolor: "rgba(255,255,255,0.98)",
            zIndex: 900,
            pointerEvents: "none"
          }}
        />

        <Box
          sx={{
            position: "fixed",
            left: 16,
            bottom: 5,
            zIndex: 1000,
            display: "flex",
            gap: 1
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleNavigate()}
          >
            SHOW
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ ml: 1 }}
            onClick={() => handleNavigateAdd()}
          >
            BUS +
          </Button>
        </Box>
      </Box>

      {/* Dialog for editing status and info */}
      <Dialog open={dialogOpen} onClose={handleDialogCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Status & Info bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ mb: 1, fontWeight: 800 }}>Bus: {dialogBus}</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography sx={{ fontWeight: 500 }}>Status:</Typography>
              <RadioGroup
                row
                value={String(dialogStatus)}
                onChange={(e) => setDialogStatus(Number(e.target.value))}
                sx={{ gap: 1, mt: 0 }}
              >
                {/* White (border black) */}
                <FormControlLabel
                  value="0"
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <Box sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'transparent',
                          border: '6px solid #000',
                          boxSizing: 'border-box'
                        }} />
                      }
                      checkedIcon={
                        <Box sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'transparent',
                          border: '6px solid #000',
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#000' }} />
                        </Box>
                      }
                      inputProps={{ 'aria-label': 'weiß' }}
                      sx={{ p: 0 }}
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />

                {/* Yellow */}
                <FormControlLabel
                  value="1"
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <Box sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'transparent',
                          border: '6px solid #fbc02d',
                          boxSizing: 'border-box'
                        }} />
                      }
                      checkedIcon={
                        <Box sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'transparent',
                          border: '6px solid #fbc02d',
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#000' }} />
                        </Box>
                      }
                      inputProps={{ 'aria-label': 'gelb' }}
                      sx={{ p: 0 }}
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
                {/* Red */}
                <FormControlLabel
                  value="2"
                  control={
                    <Radio
                      disableRipple
                      icon={
                        <Box sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'transparent',           // match others: transparent fill
                          border: '6px solid #f44336',      // red border like others use colored border
                          boxSizing: 'border-box'
                        }} />
                      }
                      checkedIcon={
                        <Box sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: 'transparent',
                          border: '6px solid #f44336',
                          boxSizing: 'border-box',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#000' }} />
                        </Box>
                      }
                      inputProps={{ 'aria-label': 'rot' }}
                      sx={{ p: 0 }}
                    />
                  }
                  label=""
                  sx={{ m: 0 }}
                />
              </RadioGroup>
            </Box>

            <TextField
              label="Info (max 300 Zeichen)"
              value={dialogInfo}
              onChange={(e) => {
                const v = e.target.value.slice(0, 300);
                setDialogInfo(v);
              }}
              multiline
              rows={4}
              fullWidth
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel} disabled={dialogLoading}>Abbrechen</Button>
          <Button onClick={handleDialogSave} variant="contained" disabled={dialogLoading}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}